import { NotificationType } from 'hooks/useNotification';
import type { OrgEventNotifyConfig } from 'hooks/orgEvents/orgEventAutomationHooks';

export const MICROSOFT_TEAMS_LOCK_TOOLTIP = 'Upgrade to Enable';

export const AUTOMATION_CHANNEL_OPTIONS = [
  { label: 'PRATUS (in-app)', value: NotificationType.IN_APP },
  { label: 'Email', value: NotificationType.EMAIL },
  { label: 'SMS', value: NotificationType.SMS },
  {
    label: 'Microsoft Teams',
    value: NotificationType.TEAMS,
    disabled: true,
    tooltip: MICROSOFT_TEAMS_LOCK_TOOLTIP,
  },
  {
    label: 'Voice (TTS cell)',
    value: NotificationType.TTS_CELL,
    disabled: true,
    tooltip: MICROSOFT_TEAMS_LOCK_TOOLTIP,
  },
] as const;

/** Channels the picker shows but will not save. */
export function withoutLockedChannels(
  types: readonly string[] | undefined
): string[] {
  const locked = new Set<string>(
    AUTOMATION_CHANNEL_OPTIONS.filter(
      (option) => 'disabled' in option && option.disabled
    ).map((option) => option.value)
  );
  return (types ?? []).filter((type) => !locked.has(type));
}

export const AUTOMATION_PRIORITY_OPTIONS = [
  { label: 'Info', value: 'info' },
  { label: 'Advisory', value: 'advisory' },
  { label: 'Watch', value: 'watch' },
  { label: 'Warning', value: 'warning' },
  { label: 'Critical', value: 'critical' },
  { label: 'Stand Down Safety', value: 'stand_down_safety' },
] as const;

export const DEFAULT_UPDATE_MIN_INTERVAL_MINUTES = 60;

export const UPDATE_INTERVAL_OPTIONS = [
  { label: '15 minutes', value: 15 },
  { label: '30 minutes', value: 30 },
  { label: '1 hour', value: 60 },
  { label: '2 hours', value: 120 },
] as const;

const INTERRUPTING_CHANNELS: string[] = [
  NotificationType.SMS,
  NotificationType.TTS_CELL,
  NotificationType.TTS_HOME,
];

/**
 * What updates use until someone picks: the create channels without text and
 * voice.
 */
export function defaultUpdateNotificationTypes(
  createTypes: readonly string[] | undefined
): string[] {
  const quiet = (createTypes ?? []).filter(
    (type) => !INTERRUPTING_CHANNELS.includes(type)
  );
  return quiet.length > 0 ? quiet : [NotificationType.IN_APP];
}

/** The update channels in effect: the chosen ones, else the default. */
export function effectiveUpdateNotificationTypes(
  values: Pick<
    AutomationNotifyFormFields,
    'notificationTypes' | 'updateNotificationTypes'
  >
): string[] {
  return (
    values.updateNotificationTypes ??
    defaultUpdateNotificationTypes(values.notificationTypes)
  );
}

export type AutomationNotifyFormFields = {
  notifyOnCreate: boolean;
  notificationTypes: string[];
  /**
   * Null until someone picks, so the default keeps following the create
   * channels.
   */
  updateNotificationTypes: string[] | null;
  updateMinIntervalMinutes: number;
  userIds: string[];
  teamIds: string[];
  roleScopeKeys: string[];
  priority: string;
  confirmReceipt: boolean;
};

export function notifyFieldsFromRule(rule: {
  notify_on_create?: boolean;
  notify_config?: OrgEventNotifyConfig | null;
}): AutomationNotifyFormFields {
  const notificationTypes = withoutLockedChannels(
    rule.notify_config?.notificationTypes
  );
  const updateTypes = withoutLockedChannels(
    rule.notify_config?.updateNotificationTypes
  );
  const interval = Number(rule.notify_config?.updateMinIntervalMinutes);
  return {
    notifyOnCreate: Boolean(rule.notify_on_create),
    notificationTypes: notificationTypes.length
      ? notificationTypes
      : [NotificationType.IN_APP],
    updateNotificationTypes: updateTypes.length ? updateTypes : null,
    updateMinIntervalMinutes:
      Number.isFinite(interval) && interval >= 0
        ? interval
        : DEFAULT_UPDATE_MIN_INTERVAL_MINUTES,
    userIds: rule.notify_config?.userIds ?? [],
    teamIds: rule.notify_config?.teamIds ?? [],
    roleScopeKeys: rule.notify_config?.roleScopeKeys ?? [],
    priority: rule.notify_config?.priority || 'watch',
    confirmReceipt: Boolean(rule.notify_config?.confirmReceipt),
  };
}

/** The body a new rule sends, matching `buildNotifyConfigPayload`. */
export const SMS_PREVIEW_BODY_TEMPLATE =
  '{eventName}: {stormName} impacts {resourceCount} resources.';

/**
 * Same length as a real short link, so the character count matches what is
 * sent.
 */
export function sampleShortLink(): string {
  const origin =
    typeof window === 'undefined'
      ? 'https://app.pratus.ai'
      : window.location.origin;
  return `${origin}/n/xxxxxxxxxx`;
}
export const LINKED_SMS_MAX_LENGTH = 300;

/**
 * The text message as it goes out: body, then the short link, cut to fit with
 * the link kept.
 */
export function smsPreviewWithLink(
  body: string,
  link: string = sampleShortLink()
): string {
  const line = `Open in PRATUS: ${link}`;
  const text = body.trim();
  const combined = text ? `${text}\n\n${line}` : line;
  if (combined.length <= LINKED_SMS_MAX_LENGTH) return combined;
  const room = LINKED_SMS_MAX_LENGTH - line.length - 3;
  const trimmed = room > 0 ? `${text.slice(0, room).trimEnd()}…` : '';
  return trimmed ? `${trimmed}\n\n${line}` : line;
}

export function buildNotifyConfigPayload(
  values: AutomationNotifyFormFields,
  existing?: OrgEventNotifyConfig | null
): OrgEventNotifyConfig {
  const notificationTypes = withoutLockedChannels(values.notificationTypes);
  const updateTypes = withoutLockedChannels(
    values.updateNotificationTypes ?? undefined
  );
  return {
    ...existing,
    notificationTypes: notificationTypes.length
      ? notificationTypes
      : [NotificationType.IN_APP],
    updateNotificationTypes:
      values.updateNotificationTypes && updateTypes.length
        ? updateTypes
        : undefined,
    updateMinIntervalMinutes:
      values.updateMinIntervalMinutes ?? DEFAULT_UPDATE_MIN_INTERVAL_MINUTES,
    userIds: values.userIds ?? [],
    teamIds: values.teamIds ?? [],
    roleScopeKeys: values.roleScopeKeys ?? [],
    priority: values.priority || 'watch',
    confirmReceipt: Boolean(values.confirmReceipt),
    titleTemplate: existing?.titleTemplate || 'Threshold event: {eventName}',
    bodyTemplate:
      existing?.bodyTemplate ||
      '{eventName}: {stormName} impacts {resourceCount} resources.',
  };
}
